import api from './api';

export interface FinancialSummary {
  total_cases: number;
  total_revenue: number;
  total_retainer_received: number;
  total_balance_outstanding: number;
  paid_cases: number;
  unpaid_cases: number;
  rush_cases: number;
  average_case_value: number;
}

export interface FinancialCase {
  id: number;
  case_name: string;
  firm_name: string;
  service_type: string;
  created_by: string;
  assigned_expert: string;
  status: string;
  payment_status: string;
  is_rush: boolean;
  service_type_price: number;
  rush_fee_amount: number;
  total_amount: number;
  retainer_amount: number;
  balance_amount: number;
  created_at: string;
  due_date: string;
  invoice_date?: string;
  payment_date?: string;
}

export interface DetailedReport {
  summary: {
    total_cases: number;
    total_revenue: number;
    total_retainer_received: number;
    total_balance_outstanding: number;
    total_service_fees: number;
    total_rush_fees: number;
  };
  cases: FinancialCase[];
}

export interface ServiceTypeStats {
  service_type_id: number;
  service_type_name: string;
  total_cases: number;
  total_revenue: number;
  total_service_fees: number;
  total_rush_fees: number;
  rush_cases: number;
  average_case_value: number;
  paid_cases: number;
  unpaid_cases: number;
}

export interface FirmStats {
  firm_id: number;
  firm_name: string;
  total_cases: number;
  total_revenue: number;
  total_retainer_received: number;
  total_balance_outstanding: number;
  rush_cases: number;
  average_case_value: number;
  paid_cases: number;
  unpaid_cases: number;
}

class FinancialReportService {
  /**
   * Get financial summary for dashboard
   */
  async getFinancialSummary(params?: {
    start_date?: string;
    end_date?: string;
  }): Promise<FinancialSummary> {
    try {
      const response = await api.get('/financial-reports/summary', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching financial summary:', error);
      throw error;
    }
  }

  /**
   * Get detailed financial report
   */
  async getDetailedReport(params?: {
    firm_id?: number;
    service_type_id?: number;
    status?: string;
    payment_status?: string;
    start_date?: string;
    end_date?: string;
  }): Promise<DetailedReport> {
    try {
      const response = await api.get('/financial-reports/detailed', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching detailed report:', error);
      throw error;
    }
  }

  /**
   * Export financial report as CSV
   */
  async exportReport(params?: {
    firm_id?: number;
    service_type_id?: number;
    status?: string;
    start_date?: string;
    end_date?: string;
  }): Promise<Blob> {
    try {
      const response = await api.get('/financial-reports/export', { 
        params,
        responseType: 'blob'
      });
      return response.data;
    } catch (error) {
      console.error('Error exporting report:', error);
      throw error;
    }
  }

  /**
   * Get financial statistics by service type
   */
  async getServiceTypeStats(params?: {
    start_date?: string;
    end_date?: string;
  }): Promise<ServiceTypeStats[]> {
    try {
      const response = await api.get('/financial-reports/service-type-stats', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching service type stats:', error);
      throw error;
    }
  }

  /**
   * Get financial statistics by firm (admin only)
   */
  async getFirmStats(params?: {
    start_date?: string;
    end_date?: string;
  }): Promise<FirmStats[]> {
    try {
      const response = await api.get('/financial-reports/firm-stats', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching firm stats:', error);
      throw error;
    }
  }

  /**
   * Download CSV file
   */
  downloadCSV(blob: Blob, filename: string) {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }
}

export default new FinancialReportService(); 